/*
 * Algolia InstantSearch customizations (Algolia AI Search & Discovery app).
 *
 * - Collection + search pages: replaces the default InstantSearch layout with
 *   Adda's toolbar (filters pill + sort pill), a right-side filters drawer and a
 *   grid that mirrors the native product grid. Headers stay in Liquid
 *   (main-collection-product-grid with "enable_algolia", main-search-algolia).
 * - Collection + search pages: renders hits with Adda's product card markup.
 *
 * Settings come from #AddaAlgoliaConfig (rendered by the section) so the
 * theme editor stays the source of truth.
 */
(function () {
  function readJson(id) {
    var el = document.getElementById(id);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function readConfig() {
    return readJson('AddaAlgoliaConfig');
  }

  var config = readConfig();
  var sizeLabels = readJson('AddaAlgoliaSizes');

  // Adda's layout applies wherever a section rendered #AddaAlgoliaConfig:
  // main-collection-product-grid (enable_algolia) or main-search-algolia.
  function useAddaLayout() {
    return Boolean(document.getElementById('AddaAlgoliaConfig'));
  }

  /* ---------- Formatting ---------- */

  function formatMoney(value) {
    var amount = Number(value) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: amount % 1 ? 2 : 0,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace(/\u00a0/g, ' ');
  }

  function firstValue(value) {
    return Array.isArray(value) ? value[0] : value;
  }

  // Metaobject references are indexed as "gid://shopify/Metaobject/<id>";
  // snippets/algolia-size-labels.liquid maps ids to labels.
  function sizeLabel(value) {
    var size = firstValue(value);
    if (typeof size !== 'string' || size.indexOf('gid://') !== 0) return size;
    return sizeLabels[size.split('/').pop()] || '';
  }

  function getDeduplicatedTitle(title, vendor) {
    if (!title) return '';
    if (!vendor) return title;
    var v = vendor.trim();
    var t = title.trim();
    var vRegex = new RegExp('\\b' + v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i');
    if (vRegex.test(t)) {
      return t;
    }
    return t + ' ' + v;
  }

  // Shopify CDN resizing (same widths the native card uses).
  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function imageSrcset(src) {
    return [165, 250, 360, 533, 720, 940, 1066]
      .map(function (w) {
        return imageUrl(src, w) + ' ' + w + 'w';
      })
      .join(', ');
  }

  /* ---------- Filters drawer ---------- */

  // A modal <dialog> renders in the top layer, so it escapes the apps section's
  // scroll-reveal transform and the sticky header, and handles Esc/focus natively.
  function setExpanded(expanded) {
    document.querySelectorAll('[aria-controls="AddaAisDrawer"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', String(expanded));
    });
  }

  function openDrawer() {
    var drawer = document.getElementById('AddaAisDrawer');
    if (!drawer || drawer.open) return;
    drawer.showModal();
    setExpanded(true);
  }

  function closeDrawer() {
    var drawer = document.getElementById('AddaAisDrawer');
    if (drawer && drawer.open) drawer.close();
  }

  // Clicks on the ::backdrop target the <dialog> itself.
  function onDrawerClick(event) {
    if (event.target === event.currentTarget) closeDrawer();
  }

  function clearRefinements() {
    var clear = document.querySelector('#AddaAisDrawer .ais-ClearRefinements-button');
    if (clear) clear.click();
  }

  /* ---------- Card images ---------- */

  // Hits don't carry image dimensions, so cards keep the native portrait shape
  // and the photo's shape is checked once it loads: square/landscape photos
  // (glasses, jewellery) are shown whole instead of being cropped by `cover`.
  // Images stay transparent until then so the switch is never visible.
  var CONTAIN_MIN_RATIO = 0.8; // width / height

  function fitCardImage(eventOrImg) {
    var img = eventOrImg && eventOrImg.currentTarget ? eventOrImg.currentTarget : eventOrImg;
    if (!img || !img.complete) return; // ref fires before load; onLoad follows
    if (img.naturalWidth && img.naturalWidth / img.naturalHeight >= CONTAIN_MIN_RATIO) {
      img.classList.add('is-contained');
    }
    img.classList.add('is-ready');
  }

  /* ---------- Sort pill ---------- */

  // Algolia's SortBy <select> stays mounted (hidden) as the source of truth; the
  // pill reuses the native .adda-sort-pill-* component and drives the select.
  // The first option is Algolia's primary index ("Relevance", which Algolia
  // won't let you remove or rename per collection), so it gets the theme's label.
  function getSortSelect() {
    return document.querySelector('.adda-ais .ais-SortBy-select');
  }

  function sortLabel(select, index) {
    if (index === 0 && config.defaultSortLabel) return config.defaultSortLabel;
    return select.options[index].text;
  }

  function syncSortPill() {
    var select = getSortSelect();
    var current = document.getElementById('AddaAisSortCurrent');
    if (!select || !current || select.selectedIndex < 0) return;
    current.textContent = sortLabel(select, select.selectedIndex);
  }

  function renderSortOptions() {
    var select = getSortSelect();
    var list = document.getElementById('AddaAisSortOptions');
    if (!select || !list) return;
    list.textContent = '';
    Array.prototype.forEach.call(select.options, function (option, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'adda-sort-pill-option' + (option.selected ? ' is-active' : '');
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(option.selected));
      button.dataset.value = option.value;
      button.textContent = sortLabel(select, index);
      list.appendChild(button);
    });
  }

  function onSortToggle(event) {
    if (event.currentTarget.open) renderSortOptions();
  }

  function onSortOptionClick(event) {
    var button = event.target.closest('.adda-sort-pill-option');
    var select = getSortSelect();
    if (!button || !select) return;
    select.value = button.dataset.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    syncSortPill();
    closeSortPill();
  }

  function closeSortPill() {
    var details = document.getElementById('AddaAisSort');
    if (details && details.open) {
      details.open = false;
      details.querySelector('summary').focus();
    }
  }

  document.addEventListener('click', function (event) {
    var details = document.getElementById('AddaAisSort');
    if (details && details.open && !details.contains(event.target)) details.open = false;
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeSortPill();
  });

  /* ---------- "Ver mais" / infinite scroll ---------- */

  // Replaces Algolia's numbered pagination. Instead of paging, it grows
  // hitsPerPage by one page per click, so Algolia's own hits widget keeps
  // rendering (card template, click analytics and positions stay intact) and
  // no page number lands in the URL: refresh/back return to the first batch,
  // like the native Ajaxinate button. Any other state change (filter, sort)
  // resets to one page before that search is sent.
  var MAX_HITS_PER_PAGE = 1000; // Algolia's hard limit

  var loadMore = { helper: null, base: 0, stateKey: null, pending: false, observer: null };

  function stateKey(state) {
    return JSON.stringify(state, function (key, value) {
      return key === 'hitsPerPage' || key === 'page' ? undefined : value;
    });
  }

  function onLoadMore() {
    var helper = loadMore.helper;
    var button = document.querySelector('#AddaAisMore button');
    if (!helper || loadMore.pending) return;
    var next = Math.min(
      (helper.state.hitsPerPage || loadMore.base) + loadMore.base,
      MAX_HITS_PER_PAGE,
    );
    loadMore.pending = true;
    if (button) {
      button.disabled = true;
      button.textContent = 'Carregando…';
    }
    helper.setQueryParameter('hitsPerPage', next).search();
  }

  function observeLoadMore(container) {
    if (config.loadMore !== 'infinite' || loadMore.observer || !('IntersectionObserver' in window))
      return;
    loadMore.observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting && !container.hidden) onLoadMore();
      },
      { rootMargin: '0px 0px 600px 0px' },
    );
    loadMore.observer.observe(container);
  }

  function loadMoreWidget() {
    return {
      $$type: 'adda.loadMore',
      init: function (options) {
        var helper = options.helper;
        loadMore.helper = helper;
        loadMore.base = helper.state.hitsPerPage || config.hitsPerPage || 32;
        loadMore.stateKey = stateKey(helper.state);
        // 'change' fires synchronously before refine() calls search(), so the
        // reset rides along with the filter/sort request instead of a second one.
        helper.on('change', function (event) {
          var key = stateKey(event.state);
          if (key === loadMore.stateKey) return;
          loadMore.stateKey = key;
          if (event.state.hitsPerPage !== loadMore.base) {
            helper.setQueryParameter('hitsPerPage', loadMore.base);
          }
        });
      },
      render: function (options) {
        var results = options.results;
        var container = document.getElementById('AddaAisMore');
        if (!results || !container) return;
        var button = container.querySelector('button');
        loadMore.pending = false;
        container.hidden =
          results.hits.length >= results.nbHits || results.hitsPerPage >= MAX_HITS_PER_PAGE;
        button.disabled = false;
        button.textContent = 'Ver mais';
        observeLoadMore(container);
      },
    };
  }

  /* ---------- Error Handling ---------- */

  function errorHandlerWidget() {
    return {
      $$type: 'adda.errorHandler',
      init: function (options) {
        var helper = options.helper;
        var instantSearchInstance = options.instantSearchInstance;

        function handleError(error) {
          console.error('[Algolia InstantSearch Error]:', error);
          document.documentElement.classList.add('adda-ais-ready');
          document.documentElement.classList.add('adda-ais-error');
          var container = document.querySelector('.ais-hits-container');
          if (container && !container.querySelector('.ais-Hits-list')) {
            var msg = (error && (error.message || String(error))) || '';
            var isQuota = msg.indexOf('quota') !== -1 || (error && error.status === 403);
            container.innerHTML =
              '<div class="adda-ais-empty adda-ais-error" style="text-align: center; padding: 60px 20px;">' +
              '<p class="adda-ais-empty__eyebrow" style="color: #990000; font-weight: 600;">Aviso</p>' +
              '<p class="adda-ais-empty__title" style="margin: 12px 0; font-size: 18px;">' +
              (isQuota
                ? 'Limite de operações de busca excedido no Algolia.'
                : 'Não foi possível carregar os produtos.') +
              '</p>' +
              '<p class="adda-ais-empty__text" style="color: #666; max-width: 500px; margin: 0 auto;">' +
              (isQuota
                ? 'A cota mensal de buscas foi atingida. Por favor, atualize o plano no painel do Algolia ou desative o Algolia nas configurações do tema.'
                : 'Por favor, tente recarregar a página.') +
              '</p>' +
              '</div>';
          }
        }

        if (helper && typeof helper.on === 'function') {
          helper.on('error', handleError);
        }
        if (instantSearchInstance && typeof instantSearchInstance.on === 'function') {
          instantSearchInstance.on('error', handleError);
        }
      },
      render: function () {},
    };
  }

  /* ---------- Count ---------- */

  function countLabel(count) {
    return count + ' ' + (count === 1 ? 'peça' : 'peças');
  }

  /* ---------- Hooks ---------- */

  function findChild(node, className) {
    if (!node || typeof node !== 'object') return null;
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) {
        var found = findChild(node[i], className);
        if (found) return found;
      }
      return null;
    }
    var cls = node.props && node.props.class;
    if (cls && (' ' + cls + ' ').indexOf(' ' + className + ' ') !== -1) return node;
    return node.props ? findChild(node.props.children, className) : null;
  }

  function mainTemplate(defaultTemplate, _data, html) {
    if (!useAddaLayout()) return defaultTemplate;

    // Reuse Algolia's own subtrees so every widget container keeps its exact markup.
    var facets = findChild(defaultTemplate, 'ais-facets');
    var pagination = findChild(defaultTemplate, 'ais-pagination-container');
    // Algolia's init binds listeners to these even though Adda doesn't show them,
    // so they must exist (`.ais-change-display` must stay inside `.ais-search-header`).
    var unused = ['ais-h2', 'ais-input', 'ais-facets-button'].map(function (cls) {
      return findChild(defaultTemplate, cls);
    });
    var changeDisplay = findChild(defaultTemplate, 'ais-change-display');

    // The root deliberately isn't `.ais-page`: Algolia's `.ais-page div/span/li {font-size: medium;
    // margin: 0; padding: 0}` reset outranks the theme's card styles. Only the facets keep it.
    return html`
      <div class="adda-ais">
        <div class="adda-ais__unused" hidden>
          ${unused}
          <div class="ais-search-header">${changeDisplay}</div>
        </div>

        <div class="adda-ais__toolbar">
          <div class="adda-ais__toolbar-start">
            <button
              type="button"
              class="adda-pill-btn adda-ais__filters-open"
              aria-controls="AddaAisDrawer"
              aria-expanded="false"
              onClick=${openDrawer}
            >
              <svg
                class="icon icon-filter"
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  fill="currentColor"
                  fill-rule="evenodd"
                  d="M4.833 6.5a1.667 1.667 0 1 1 3.334 0 1.667 1.667 0 0 1-3.334 0M4.05 7H2.5a.5.5 0 0 1 0-1h1.55a2.5 2.5 0 0 1 4.9 0h8.55a.5.5 0 0 1 0 1H8.95a2.5 2.5 0 0 1-4.9 0m11.117 6.5a1.667 1.667 0 1 0-3.334 0 1.667 1.667 0 0 0 3.334 0M13.5 11a2.5 2.5 0 0 1 2.45 2h1.55a.5.5 0 0 1 0 1h-1.55a2.5 2.5 0 0 1-4.9 0H2.5a.5.5 0 0 1 0-1h8.55a2.5 2.5 0 0 1 2.45-2"
                ></path>
              </svg>
              Mostrar filtros
            </button>
            <span class="adda-ais__count ais-stats-container" role="status"></span>
          </div>

          <div class="adda-ais__sort">
            <span class="adda-ais__sort-label" id="AddaAisSortLabel">Ordenar por</span>
            <details class="adda-sort-pill-details" id="AddaAisSort" onToggle=${onSortToggle}>
              <summary class="adda-sort-pill-trigger" aria-describedby="AddaAisSortLabel">
                <span class="adda-sort-pill-current" id="AddaAisSortCurrent"
                  >${config.defaultSortLabel || ''}</span
                >
                <span class="svg-wrapper adda-sort-pill-caret-inner">
                  <svg
                    class="icon icon-caret"
                    aria-hidden="true"
                    focusable="false"
                    viewBox="0 0 10 6"
                  >
                    <path
                      fill="currentColor"
                      fill-rule="evenodd"
                      d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </span>
              </summary>
              <div
                class="adda-sort-pill-options"
                id="AddaAisSortOptions"
                role="listbox"
                aria-labelledby="AddaAisSortLabel"
                onClick=${onSortOptionClick}
              ></div>
            </details>
            <span class="ais-sort-orders-container" hidden></span>
          </div>
        </div>

        <dialog
          class="adda-ais__drawer"
          id="AddaAisDrawer"
          aria-labelledby="AddaAisDrawerTitle"
          onClick=${onDrawerClick}
          onClose=${() => setExpanded(false)}
        >
          <div class="adda-ais__drawer-inner">
            <div class="adda-ais__drawer-header">
              <div class="adda-ais__drawer-heading">
                <h2 class="adda-ais__drawer-title" id="AddaAisDrawerTitle">Filtros</h2>
                <p class="adda-ais__drawer-count" data-adda-ais-count></p>
              </div>
              <button type="button" class="adda-ais__drawer-clear" onClick=${clearRefinements}>
                Limpar filtros
              </button>
              <button
                type="button"
                class="adda-ais__drawer-close"
                aria-label="Fechar filtros"
                onClick=${closeDrawer}
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>
            <div class="adda-ais__drawer-body">
              <div class="ais-page adda-ais__facets">${facets}</div>
            </div>
          </div>
        </dialog>

        <div class="ais-hits-container ais-results-as-block"></div>
        <div class="load-more adda-ais__more" id="AddaAisMore" hidden>
          <button type="button" class="load-more_btn" onClick=${onLoadMore}>Ver mais</button>
        </div>
        ${pagination}
      </div>
    `;
  }

  function statsTemplate(defaultTemplate, data, html) {
    if (!useAddaLayout()) return defaultTemplate;
    // Stats render in the same pass as the first hits: swap out the Liquid skeleton
    // (snippets/algolia-collection-skeleton.liquid) in that frame.
    document.documentElement.classList.add('adda-ais-ready');
    var label = countLabel(data.nbHits);
    // The drawer header/footer repeat the count; the SortBy select re-renders in
    // this same pass, so sync the pill label after it.
    requestAnimationFrame(function () {
      document.querySelectorAll('[data-adda-ais-count]').forEach(function (el) {
        el.textContent = label;
      });
      syncSortPill();
    });
    return html`<span>${label}</span>`;
  }

  function productTemplate(_defaultTemplate, hit, html, _components, trackSearchAttribution) {
    // Metafields are indexed under `meta.<namespace>.<key>`.
    var custom = (hit.meta && hit.meta.custom) || {};
    var size = sizeLabel(custom.tamanho) || 'Único';
    var state = firstValue(custom.estado) || '';
    var condition = firstValue(custom.condition) || '';
    var extraDiscount = firstValue(custom.discount_percentage) || '';

    var salePrice = Number(hit.price) || 0;
    var compareAt = Number(hit.variants_compare_at_price_max || hit.compare_at_price) || 0;
    var available = hit.inventory_available !== false;
    var onSale = compareAt > salePrice && salePrice > 0;
    // "% OFF" is compare-at vs sale price only; the bonus is its own label and doesn't add into it.
    var discount = onSale ? Math.round(((compareAt - salePrice) * 100) / compareAt) : 0;
    // The bonus ("50%") is an automatic discount on top of the sale price; show what the cart charges,
    // like snippets/price.liquid does.
    var extraPercent = parseFloat(extraDiscount) || 0;
    var price = Math.round(salePrice * (100 - extraPercent)) / 100;
    var pixPrice = Math.round(price * 0.9 * 100) / 100;
    var minInstallmentValue = 100;
    var maxInstallments = 6;
    var maxPossibleInstallments = Math.floor(price / minInstallmentValue);
    var installments = Math.min(maxPossibleInstallments, maxInstallments);
    var installmentValue =
      installments > 0 ? Math.round((price / installments) * 100) / 100 : price;
    var cleanSize = String(size || 'Único')
      .replace(/^tamanho\s+/i, '')
      .toUpperCase();
    var displayTitle = getDeduplicatedTitle(hit.title, hit.vendor);

    var url = '/products/' + hit.handle + (hit.objectID ? '?variant=' + hit.objectID : '');
    var image = hit.product_image || hit.image;
    var badgePosition = config.badgePosition || 'top left';
    var showVendor = config.showVendor !== false;

    function onClick() {
      if (typeof trackSearchAttribution === 'function') trackSearchAttribution(hit);
    }

    var bonusIcon = html`
      <svg
        class="adda__bonus-badge-icon"
        aria-hidden="true"
        focusable="false"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    `;

    return html`
      <div
        class="card-wrapper product-card-wrapper underline-links-hover${available ? '' : ' card--sold-out'}"
      >
        ${available ? null : html`<div class="card__sold-out-label">VENDIDO</div>`}
        <div
          class="wlh-right-btn wishlisthero-floating wishlist-hero-custom-button"
          data-wlh-handle=${hit.handle}
          data-wlh-mode="icon_only"
          data-wlh-view="Collection"
        ></div>
        <div class="card card--standard card--media" style="--ratio-percent: 150%;">
          <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 150%;">
            ${
              onSale && available
                ? html`
                    <div
                      class=${'adda__sale_badge--container card__badge adda__d-flex ' + badgePosition}
                    >
                      <span class="adda__discount--badge-text">${discount}% OFF </span>
                    </div>
                  `
                : null
            }
            ${condition === 'Novo com etiqueta' ? html`<div class="card__condition-pill">na etiqueta</div>` : null}

            <div class="adda__card--info-container">
              <div class="adda__card--info-inner">
                <div class="adda__card--info-col">
                  <span>Tamanho<br /><b>${size}</b></span>
                </div>
                ${
                  state
                    ? html`
                        <div class="adda__card--info-col">
                          <span>Estado<br /><b>${state}</b></span>
                        </div>
                      `
                    : null
                }
              </div>
            </div>

            <a
              href=${url}
              class="full-unstyled-link adda-ais-card__media-link"
              tabindex="-1"
              aria-hidden="true"
              onClick=${onClick}
            >
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${
                    image
                      ? html`<img
                          src=${imageUrl(image, 720)}
                          srcset=${imageSrcset(image)}
                          sizes="(min-width: 1600px) 367px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 28px) / 2)"
                          alt=${hit.title}
                          class="motion-reduce adda-ais-card__img"
                          loading="lazy"
                          ref=${fitCardImage}
                          onLoad=${fitCardImage}
                          onError=${fitCardImage}
                          width="1000"
                          height="1500"
                        />`
                      : null
                  }
                </div>
              </div>
            </a>

            ${
              available
                ? html`
                    <button
                      type="button"
                      class="adda-quick-add-icon no-js-hidden"
                      aria-label="Comprar"
                      data-variant-id=${hit.objectID}
                    >
                      <svg
                        class="icon"
                        aria-hidden="true"
                        focusable="false"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          stroke="currentColor"
                          stroke-width="1.5"
                          d="M.75 6.75h16.5v10.5H.75zM12.5 4.25a3.5 3.5 0 0 0-7 0m0 0v2m7-2v2"
                        />
                      </svg>
                      <div class="loading__spinner hidden">
                        <svg
                          class="spinner"
                          aria-hidden="true"
                          focusable="false"
                          viewBox="0 0 66 66"
                        >
                          <circle
                            stroke-width="6"
                            cx="33"
                            cy="33"
                            r="30"
                            fill="none"
                            class="path"
                          ></circle>
                        </svg>
                      </div>
                    </button>
                  `
                : null
            }
          </div>

          <div class="card__content">
            <div class="card__information">
              ${
                showVendor && hit.vendor
                  ? html`
                      <span class="visually-hidden">Fornecedor:</span>
                      <div
                        class="adda__card--vendor caption-with-letter-spacing"
                        aria-hidden="true"
                      >
                        <span>${hit.vendor}</span>
                      </div>
                    `
                  : null
              }
              <div class="adda__card--size">TAM. ${cleanSize}</div>
              <h3 class="adda__card--heading card__heading h5">
                <a href=${url} class="full-unstyled-link card-product-title" onClick=${onClick}
                  >${displayTitle}</a
                >
              </h3>
              <div class="card-information">
                <div
                  class=${'price' + (onSale ? ' price--on-sale' : '') + (available ? '' : ' price--sold-out')}
                >
                  <div class="price__container">
                    ${
                      onSale
                        ? html`
                            <div class="adda__price--compare-row adda__card-price--compare">
                              <span class="visually-hidden visually-hidden--inline"
                                >Preço normal</span
                              >
                              <s class="price-item price-item--regular"
                                >De ${formatMoney(compareAt)}</s
                              >
                            </div>
                          `
                        : null
                    }
                    <div class="adda__price--hero-row adda__card-price--hero">
                      <span class="price-item--pix adda__card-pix-hero">
                        ${formatMoney(pixPrice)}
                        <span class="adda__price--pix-suffix">no Pix</span>
                      </span>
                      <span class="adda__pix-badge">à vista no Pix já com 10% off</span>
                    </div>
                    <div class="adda__price--support-row adda__card-price--support">
                      ou
                      ${formatMoney(price)}${
                        installments > 1
                          ? ` em até ${installments}x de ${formatMoney(installmentValue)} sem juros`
                          : ''
                      }
                    </div>
                  </div>
                </div>
                ${
                  extraDiscount && onSale
                    ? html`<div class="adda__bonus-badge">
                        ${bonusIcon}<span class="adda__bonus-badge-text"
                          >BÔNUS +${extraDiscount}</span
                        >
                      </div>`
                    : html`<div
                        class="adda__bonus-badge"
                        style="visibility: hidden;"
                        aria-hidden="true"
                      >
                        ${bonusIcon}<span class="adda__bonus-badge-text">BÔNUS +40%</span>
                      </div>`
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function instantSearchOptions(options) {
    if (useAddaLayout() && config.hitsPerPage) options.hitsPerPage = config.hitsPerPage;
    return options;
  }

  /* ---------- No results ---------- */

  // Category links come from the search popup config (Theme settings > Search).
  function noResultTemplate(defaultTemplate, html) {
    if (!useAddaLayout()) return defaultTemplate;
    var query = new URLSearchParams(window.location.search).get('q') || '';
    var links = readJson('AddaAutocompleteConfig').other || [];

    return html`
      <div class="adda-ais-empty">
        <p class="adda-ais-empty__eyebrow">Nada por aqui</p>
        <p class="adda-ais-empty__title">
          ${query ? html`Nenhuma peça para "${query}"` : 'Nenhuma peça encontrada'}
        </p>
        <p class="adda-ais-empty__text">
          Tente outra marca ou modelo, ou${' '}
          <a class="ais-hit-empty--clear-filters adda-ais-empty__clear">limpe os filtros</a>.
        </p>
        ${
          links.length
            ? html`
                <p class="adda-ais-empty__eyebrow">Navegue por categoria</p>
                <ul class="adda-ais-empty__links" role="list">
                  ${links.map(function (link) {
                    return html`<li>
                      <a href=${link.url} class="adda-search__chip">${link.title}</a>
                    </li>`;
                  })}
                </ul>
              `
            : null
        }
      </div>
    `;
  }

  function register() {
    var hooks = window.algoliaShopify && window.algoliaShopify.hooks;
    if (!hooks) return;
    hooks.registerHook('beforeInstantSearchOptions', instantSearchOptions);
    hooks.registerHook('beforeInstantSearchMainTemplate', mainTemplate);
    hooks.registerHook('beforeInstantSearchStatsTemplate', statsTemplate);
    hooks.registerHook('beforeInstantSearchProductTemplate', productTemplate);
    hooks.registerHook('beforeInstantSearchNoResultTemplate', noResultTemplate);
    // triggerHooks spreads array accumulators into separate arguments: these
    // hooks receive list items as arguments (not an array) and return an array.
    hooks.registerHook('beforeInstantSearchStartAddWidgets', function () {
      var widgets = Array.prototype.slice.call(arguments);
      if (useAddaLayout()) {
        widgets.push(loadMoreWidget());
        widgets.push(errorHandlerWidget());
      }
      return widgets;
    });
    // Called as (...widgetsToRemove, defaultWidgets); Algolia removes the
    // returned widgets and their containers after start().
    hooks.registerHook('afterInstantSearchStartRemoveDefaultWidgets', function () {
      var toRemove = Array.prototype.slice.call(arguments);
      var defaults = toRemove.pop();
      if (!useAddaLayout() || !Array.isArray(defaults)) return toRemove;
      var pagination = defaults.find(function (widget) {
        return widget.$$type === 'ais.pagination';
      });
      if (pagination) toRemove.push(pagination);
      return toRemove;
    });
  }

  if (window.algoliaShopify && window.algoliaShopify.hooks) {
    register();
  } else {
    document.addEventListener('algolia.hooks.initialize', register);
  }

  // Safety timeout: if Algolia doesn't signal ready within 7s (e.g. network failure or 403 quota),
  // dismiss the skeleton and show an informative message.
  if (useAddaLayout()) {
    setTimeout(function () {
      if (!document.documentElement.classList.contains('adda-ais-ready')) {
        document.documentElement.classList.add('adda-ais-ready');
        var hitsContainer = document.querySelector('.ais-hits-container');
        if (hitsContainer && !hitsContainer.querySelector('.ais-Hits-list')) {
          hitsContainer.innerHTML =
            '<div class="adda-ais-empty adda-ais-error" style="text-align: center; padding: 60px 20px;">' +
            '<p class="adda-ais-empty__eyebrow" style="color: #990000; font-weight: 600;">Aviso</p>' +
            '<p class="adda-ais-empty__title" style="margin: 12px 0; font-size: 18px;">' +
            'Não foi possível carregar os produtos' +
            '</p>' +
            '<p class="adda-ais-empty__text" style="color: #666; max-width: 500px; margin: 0 auto;">' +
            'A busca do Algolia não respondeu a tempo (a cota de operações pode ter sido excedida).' +
            '</p>' +
            '</div>';
        }
      }
    }, 7000);
  }
})();
